import { ReadSearchApiResponse, SearchUser } from 'lib-search-types';
import { AsyncStatus, MetaConversation } from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';
import findIndex from 'lodash/findIndex';
import { handleActions } from 'redux-actions';

import { emptyMetaConversation } from '../../../conversation/reducers/conversation';
import { transferToNumberActions } from '../actions/transferToNumberModal';

export type TransferRequestState = {
  metaConversation: MetaConversation | null;
  requestToUser: SearchUser;
  requestFromAgent: User;
};

export type TransferModalState = {
  meta: {
    requestAssistedTransfer: AsyncStatus;
    requestBlindTransfer: AsyncStatus;
    searchUser: AsyncStatus;
  };
  ui: {
    isModalShown: boolean; // If the transfer to number modal is open or not.
  };
  users: Array<SearchUser>; // The returned users from search.
  transferRequests: Array<TransferRequestState>; // All the transfer requests.
  metaConversation: MetaConversation; // current metaConversation that is being transferred.
};

export const initialState: TransferModalState = {
  meta: {
    requestAssistedTransfer: 'succeeded',
    requestBlindTransfer: 'succeeded',
    searchUser: 'succeeded',
  },
  ui: {
    isModalShown: false,
  },
  users: [],
  metaConversation: emptyMetaConversation(),
  transferRequests: [],
};

export const {
  transferToNumberAssistedRequest,
  transferToNumberAssistedRequestSucceeded,
  transferToNumberAssistedRequestFailed,

  transferToNumberBlindRequest,
  transferToNumberBlindRequestSucceeded,
  transferToNumberBlindRequestFailed,

  transferToNumberAssistedResolve,
  transferToNumberAssistedResolveSucceeded,
  transferToNumberAssistedResolveFailed,

  transferToNumberGetUsersByQuery,
  transferToNumberGetUsersByQuerySucceeded,
  transferToNumberGetUsersByQueryFailed,

  transferToNumberModalOpen,
  transferToNumberModalClose,

  transferToNumberResetUsers,

  transferToNumberRequestStop,

  // WS actions.
  transferToNumberWsResponse,
  transferToNumberWsTransferStarted,
  transferToNumberWsTransferAnswered,
} = transferToNumberActions;

const transferToNumber = handleActions<TransferModalState, any>(
  {
    // Show trasnferModal to number modal.
    [transferToNumberModalOpen.toString()]: (
      state: TransferModalState,
      action,
    ) => {
      const metaConversation = Object.assign(
        {},
        action.payload.metaConversation,
      );
      return {
        ...state,
        ui: { ...state.ui, isModalShown: true },
        metaConversation: metaConversation,
      };
    },

    // Hide assistedTransferModal modal.
    [transferToNumberModalClose.toString()]: (
      state: TransferModalState,
      action,
    ) => {
      return {
        ...state,
        ui: { ...state.ui, isModalShown: false },
        users: [],
      };
    },

    [transferToNumberResetUsers.toString()]: (
      state: TransferModalState,
      action,
    ) => {
      return { ...state, users: [] };
    },

    [transferToNumberGetUsersByQuery.toString()]: (
      state: TransferModalState,
      action,
    ) => {
      return { ...state, meta: { ...state.meta, searchUser: 'loading' } };
    },

    [transferToNumberGetUsersByQuerySucceeded.toString()]: (
      state: TransferModalState,
      action,
    ) => {
      return {
        ...state,
        meta: { ...state.meta, searchUser: 'succeeded' },
        users:
          (action.payload as ReadSearchApiResponse<'users'>).data?.results?.map(
            ({ result }) => result,
          ) ?? [],
      };
    },

    [transferToNumberGetUsersByQueryFailed.toString()]: (
      state: TransferModalState,
      action,
    ) => {
      return { ...state, meta: { ...state.meta, searchUser: 'failed' } };
    },

    [transferToNumberAssistedRequest.toString()]: (
      state: TransferModalState,
      action: {
        payload: {
          metaConversation: MetaConversation;
          requestToUser: SearchUser;
          requestFromAgent: User;
        };
      },
    ) => {
      const metaConversation = Object.assign(
        {},
        action.payload.metaConversation,
      );
      const requestToUser = Object.assign({}, action.payload.requestToUser);
      const requestFromAgent = Object.assign(
        {},
        action.payload.requestFromAgent,
      );

      const request = {
        metaConversation: metaConversation,
        requestToUser: requestToUser,
        requestFromAgent: requestFromAgent,
      };
      return {
        ...state,
        meta: { ...state.meta, requestAssistedTransfer: 'loading' },
        transferRequests: [
          request,
          ...state.transferRequests.filter(
            (t: any) =>
              t.metaConversation.conversation.id !==
              metaConversation.conversation.id,
          ),
        ],
      };
    },

    // We do not remove metaConversation here,
    // we need to wait until transferToNumberAssistedResolveSucceeded/Failed.
    // or transferToNumberAssistedRequestFailed
    [transferToNumberAssistedRequestSucceeded.toString()]: (
      state: TransferModalState,
      action,
    ) => {
      return {
        ...state,
        meta: { ...state.meta, requestAssistedTransfer: 'succeeded' },
        ui: { ...state.ui, isModalShown: false },
        users: [],
      };
    },

    // The transfer to number request failed.
    // It does not make sense to keep the metaConversation in the store.
    [transferToNumberAssistedRequestFailed.toString()]: (
      state: TransferModalState,
      action,
    ) => {
      const { metaConversation } = action.payload.action;
      const index = findIndex(
        state.transferRequests,
        (assistedTransferRequest: TransferRequestState) => {
          return (
            metaConversation !== undefined &&
            assistedTransferRequest?.metaConversation?.conversation?.id ===
              metaConversation.conversation.id
          );
        },
      );
      if (index > -1) {
        const transferRequests = state.transferRequests.slice();
        transferRequests.splice(index, 1);
        return {
          ...state,
          meta: { ...state.meta, requestAssistedTransfer: 'failed' },
          transferRequests: transferRequests,
          metaConversation: emptyMetaConversation(),
        };
      }
      return {
        ...state,
        meta: { ...state.meta, requestAssistedTransfer: 'failed' },
        metaConversation: emptyMetaConversation(),
      };
    },

    [transferToNumberBlindRequest.toString()]: (
      state: TransferModalState,
      action: {
        payload: {
          metaConversation: MetaConversation;
          requestFromAgent: User;
          requestToUser: SearchUser;
        };
      },
    ) => {
      const metaConversation = Object.assign(
        {},
        action.payload.metaConversation,
      );
      const requestToUser = Object.assign({}, action.payload.requestToUser);
      const requestFromAgent = Object.assign(
        {},
        action.payload.requestFromAgent,
      );

      const request = {
        metaConversation: metaConversation,
        requestToUser: requestToUser,
        requestFromAgent: requestFromAgent,
      };
      return {
        ...state,
        meta: { ...state.meta, requestBlindTransfer: 'loading' },
        transferRequests: [
          request,
          ...state.transferRequests.filter(
            (t: any) =>
              t.metaConversation.conversation.id !==
              metaConversation.conversation.id,
          ),
        ],
      };
    },

    [transferToNumberBlindRequestSucceeded.toString()]: (
      state: TransferModalState,
    ) => {
      return {
        ...state,
        meta: { ...state.meta, requestBlindTransfer: 'succeeded' },
        ui: { ...state.ui, isModalShown: false },
        users: [],
        metaConversation: emptyMetaConversation(),
      };
    },

    [transferToNumberBlindRequestFailed.toString()]: (
      state: TransferModalState,
      action,
    ) => {
      const { metaConversation } = action.payload.action;
      const index = findIndex(
        state.transferRequests,
        (blindTransferRequest: TransferRequestState) => {
          return (
            metaConversation !== undefined &&
            blindTransferRequest?.metaConversation?.conversation?.id ===
              metaConversation.conversation.id
          );
        },
      );
      if (index > -1) {
        const transferRequests = state.transferRequests.slice();
        transferRequests.splice(index, 1);
        return {
          ...state,
          meta: { ...state.meta, requestBlindTransfer: 'failed' },
          transferRequests: transferRequests,
          metaConversation: emptyMetaConversation(),
        };
      }
      return {
        ...state,
        meta: { ...state.meta, requestAssistedTransfer: 'failed' },
        metaConversation: emptyMetaConversation(),
      };
    },

    [transferToNumberAssistedResolve.toString()]: (
      state: TransferModalState,
      action,
    ) => {
      return {
        ...state,
        meta: { ...state.meta, requestAssistedTransfer: 'loading' },
      };
    },

    // The transfer to number was resolved.
    // We cleanup the store (by removing the metaConversation).
    [transferToNumberAssistedResolveSucceeded.toString()]: (
      state: TransferModalState,
      action: { payload: { data: { csid: number } } },
    ) => {
      const csid = action.payload.data.csid;

      return {
        ...state,
        meta: { ...state.meta, requestAssistedTransfer: 'succeeded' },
        transferRequests: [
          ...state.transferRequests.filter(
            (t: any) => t.metaConversation.conversation.id !== csid,
          ),
        ],
        metaConversation: emptyMetaConversation(),
      };
    },

    // The transfer to number resolve request failed.
    // We remove the metaConversation from the store.
    [transferToNumberAssistedResolveFailed.toString()]: (
      state: TransferModalState,
    ) => {
      return {
        ...state,
        meta: { ...state.meta, requestAssistedTransfer: 'failed' },
        metaConversation: emptyMetaConversation(),
      };
    },

    // The transfer to number request was stop (maybe because the conversation was closed/claimed).
    // We remove the metaConversation from the store and closes the transfer modal.
    [transferToNumberRequestStop.toString()]: (
      state: TransferModalState,
      action: { payload: { csid: number } },
    ) => {
      const { csid } = action.payload;
      const transferRequests = state.transferRequests.slice();
      const index = findIndex(state.transferRequests, (request) => {
        return request.metaConversation?.conversation.id === csid;
      });
      if (index > -1) {
        transferRequests.splice(index, 1);
        return {
          ...state,
          transferRequests: transferRequests,
          ui: { ...state.ui, isModalShown: false },
          users: [],
          metaConversation: emptyMetaConversation(),
        };
      }
      return {
        ...state,
        ui: { ...state.ui, isModalShown: false },
        users: [],
        metaConversation: emptyMetaConversation(),
      };
    },

    // Fired when conversation:transfer_status is received on WS.
    // params.accepted has to be either true/false.
    [transferToNumberWsResponse.toString()]: (
      state: TransferModalState,
      action: { payload: { csid: number; accepted: boolean } },
    ) => {
      const { csid, accepted } = action.payload;
      const transferRequests = state.transferRequests.slice();

      const index = findIndex(state.transferRequests, (request) => {
        return (
          request.metaConversation?.conversation.id === csid &&
          (accepted === true || accepted === false)
        );
      });

      const ui = Object.assign({}, state.ui);
      let users = state.users.slice();
      // If the modal is open and the transfer is completed
      if (
        ui.isModalShown === true &&
        (accepted === true || accepted === false) &&
        csid === state.metaConversation.conversation.id
      ) {
        ui.isModalShown = false;
        users = [];
      }

      if (index > -1) {
        transferRequests.splice(index, 1);
        return {
          ...state,
          ui: ui,
          transferRequests: transferRequests,
          users: users,
          metaConversation: emptyMetaConversation(),
        };
      }
      return { ...state, ui: ui, users: users };
    },
  },
  initialState,
);

export default transferToNumber;
